/**
 * components/client/ClientToolbar.tsx
 *
 * Thin toolbar displayed between the progress bar and the branding header
 * during active form filling. Contains:
 * - "Quitter" / "Sauvegarder et quitter" button (left) — styled as a visible button
 * - SaveIndicator showing live save status (right)
 *
 * Hidden when the keyboard is open to maximize vertical space.
 *
 * Updated June 2026: Quit action now renders as a proper styled button with
 * LogOut icon and text label, making it clearly interactive on mobile.
 * Text is "Quitter" on small screens, "Sauvegarder et quitter" on sm+.
 */

import { LogOut } from 'lucide-react';
import SaveIndicator from './SaveIndicator';

interface ClientToolbarProps {
  isSaving: boolean;
  isKeyboardOpen: boolean;
  onQuit: () => void;
}

export default function ClientToolbar({ isSaving, isKeyboardOpen, onQuit }: ClientToolbarProps) {
  if (isKeyboardOpen) return null;

  return (
    <div className="flex-shrink-0 flex items-center justify-between px-4 py-2 bg-white/60 backdrop-blur-sm border-b border-neutral-100/80">
      <button
        onClick={onQuit}
        className="flex items-center gap-1.5 bg-neutral-100 hover:bg-neutral-200 active:bg-neutral-300 text-neutral-600 hover:text-neutral-800 transition-colors py-1.5 px-3 rounded-lg touch-manipulation"
        aria-label="Sauvegarder et quitter"
      >
        <LogOut className="w-3.5 h-3.5 flex-shrink-0" />
        <span className="text-xs font-medium sm:hidden">Quitter</span>
        <span className="text-xs font-medium hidden sm:inline">Sauvegarder et quitter</span>
      </button>

      <SaveIndicator isSaving={isSaving} />
    </div>
  );
}
